import { useEffect, useRef, useState, type ReactNode } from 'react'
import cn from 'clsx'

import { Button } from '@ds/components'

import { COLLECTION_LABELS } from '../../constants/collectionStaticCopy'

import styles from './CollectionDescription.module.css'

export type TCollectionDescriptionProps = {
    description: string
    /** Replaces the raw text when the author is allowed clickable links. */
    descriptionSlot?: ReactNode
    placeholder: ReactNode
    /** Up to desktop the tags live in here, revealed by the toggle. */
    tagsSlot?: ReactNode
}

export const CollectionDescription = ({
    description,
    descriptionSlot,
    placeholder,
    tagsSlot,
}: TCollectionDescriptionProps) => {
    const textRef = useRef<HTMLParagraphElement>(null)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        if (textRef.current) {
            setIsOverflowing(textRef.current.scrollHeight > textRef.current.offsetHeight)
        }
    }, [])

    const handleExpand = () => setIsExpanded(true)
    const handleCollapse = () => setIsExpanded(false)

    // The toggle also exists purely to reveal the tags, and then it belongs to the narrow screens only.
    const hasToggle = isOverflowing || Boolean(tagsSlot)
    const toggleClassName = cn(styles.toggle, {
        [styles.toggleTagsOnly]: !isOverflowing && Boolean(tagsSlot),
    })

    return (
        <div className={styles.description}>
            <p ref={textRef} className={cn(styles.text, { [styles.clamped]: !isExpanded })}>
                {description ? (
                    (descriptionSlot ?? description)
                ) : (
                    <span className={styles.placeholder}>{placeholder}</span>
                )}
            </p>

            {isExpanded && tagsSlot ? <div className={styles.tags}>{tagsSlot}</div> : null}

            {hasToggle && !isExpanded ? (
                <Button
                    className={toggleClassName}
                    variant="tertiary"
                    size="small"
                    onClick={handleExpand}
                >
                    {COLLECTION_LABELS.showDetails}
                </Button>
            ) : null}

            {hasToggle && isExpanded ? (
                <Button
                    className={toggleClassName}
                    variant="tertiary"
                    size="small"
                    onClick={handleCollapse}
                >
                    {COLLECTION_LABELS.showLessDetails}
                </Button>
            ) : null}
        </div>
    )
}
